// Flujo de "entrar/salir" de una frecuencia secreta.
//
// Filosofía Syphrax: una freq es una identidad COMPLETAMENTE INDEPENDIENTE
// para el server. El cliente, al entrar en una freq:
//   1. Deriva determinísticamente (pseudonym, password, keypair, wrappingKey)
//      a partir de (realSyphraxId, freqSecret).
//   2. Hace register de la pseudo-cuenta. Si ya existía → 409 → login.
//   3. Recibe un JWT NUEVO ligado a la pseudo-cuenta. El JWT base se aparca.
//   4. Hidrata chats/ratchet/prefs cifrados de la pseudo-cuenta (con su wrappingKey).
//   5. Devuelve una Session con `pseudoOf` rellenado.
//
// Salir de la freq: descartamos la pseudo-sesión y reactivamos el JWT base.

import { api, setAuthToken } from './api';
import * as crypto from './crypto';
import { deriveFrequencyAccount } from './identity_freq';
import type { Session } from './screens/Auth';

export async function enterFrequency(
  baseSession: Session,
  freqSecret: string,
  freqLabel: string,
): Promise<Session> {
  await crypto.ready();
  const acc = deriveFrequencyAccount(baseSession.realSyphraxId, freqSecret);

  // Wrap priv key con la wrappingKey HKDF de la pseudo. El server lo guarda
  // (formato compatible con register endpoint) pero el cliente NO lo necesita
  // al re-login: las claves se rederivan determinísticamente desde freqSecret.
  const wrappedPriv = crypto.wrapPrivateKey(acc.privateKey, acc.wrappingKey);

  let auth;
  try {
    auth = await api.register({
      username: acc.pseudonym,
      password: acc.authPassword,
      public_key: crypto.b64encode(acc.publicKey),
      private_key_encrypted: crypto.b64encode(wrappedPriv.ciphertext),
      private_key_salt: crypto.b64encode(acc.pseudoSalt),
    });
  } catch (e: any) {
    if ((e as Error).message?.includes('HTTP 409')) {
      auth = await api.login(acc.pseudonym, acc.authPassword);
    } else {
      throw e;
    }
  }

  setAuthToken(auth.token);

  return {
    syphraxId: acc.pseudonym,
    realSyphraxId: baseSession.realSyphraxId,
    publicKey: acc.publicKey,
    privateKey: acc.privateKey,
    token: auth.token,
    wrappingKey: acc.wrappingKey,
    pseudoOf: { freqSecret, freqLabel },
  };
}

/**
 * Restaura la sesión base. El JWT base puede haber expirado mientras estaba
 * en la freq; si así es, el siguiente request 401 disparará el flujo de
 * auto-clean y forzará re-login.
 */
export function leaveFrequency(baseSession: Session): Session {
  setAuthToken(baseSession.token);
  return baseSession;
}
